import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VisualCalendar, CalendarSyncButton } from "@/components/calendar";
import { AvailabilitySettings } from "@/components/calendar/AvailabilitySettings";
import { ScheduleMeetingForm } from "@/components/partners/ScheduleMeetingForm";
import { Calendar, Video, Clock, User, Plus, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Meeting {
  id: string;
  clientId: string;
  clientName: string;
  mandateId?: string;
  title: string;
  description?: string;
  scheduledAt: any;
  duration: number;
  meetingLink: string;
  meetingType?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  attendees: string[];
}

const mockMeetings: Meeting[] = [
  {
    id: "meeting-1",
    clientId: "client-1",
    clientName: "Jean-Pierre Martin",
    title: "Initial Consultation",
    description: "Review case details and requirements",
    scheduledAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    duration: 60,
    meetingLink: "https://meet.jit.si/consultation-abc123",
    meetingType: "video",
    status: "scheduled",
    attendees: ["client@example.com", "partner@example.com"],
  },
  {
    id: "meeting-2",
    clientId: "client-2",
    clientName: "Sophie Tremblay",
    title: "Document Review",
    scheduledAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    duration: 45,
    meetingLink: "https://meet.jit.si/review-def456",
    meetingType: "video",
    status: "scheduled",
    attendees: ["sophie@example.com", "partner@example.com"],
  },
];

const translations = {
  en: {
    title: "Calendar",
    subtitle: "Manage your schedule and meetings",
    newMeeting: "New Meeting",
    calendarView: "Calendar View",
    upcomingMeetings: "Upcoming Meetings",
    availability: "Availability",
    noMeetings: "No upcoming meetings",
    scheduledMeetings: "scheduled meetings",
    joinMeeting: "Join Meeting",
    viewDetails: "View Details",
    duration: "Duration",
    minutes: "min",
    today: "Today",
    tomorrow: "Tomorrow",
    scheduled: "Scheduled",
    completed: "Completed",
    cancelled: "Cancelled",
  },
  fr: {
    title: "Calendrier",
    subtitle: "Gérez votre emploi du temps et vos réunions",
    newMeeting: "Nouvelle Réunion",
    calendarView: "Vue Calendrier",
    upcomingMeetings: "Réunions à Venir",
    availability: "Disponibilités",
    noMeetings: "Aucune réunion à venir",
    scheduledMeetings: "réunions planifiées",
    joinMeeting: "Rejoindre",
    viewDetails: "Voir Détails",
    duration: "Durée",
    minutes: "min",
    today: "Aujourd'hui",
    tomorrow: "Demain",
    scheduled: "Planifiée",
    completed: "Terminée",
    cancelled: "Annulée",
  },
};

export default function PartnerCalendar() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("calendar");
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  useEffect(() => {
    if (user) {
      loadMeetings();
    }
  }, [user]);

  const loadMeetings = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const meetingsQuery = query(
        collection(db, 'partnerMeetings'),
        where('partnerId', '==', user.email),
        orderBy('scheduledAt', 'desc')
      );
      const meetingsSnapshot = await getDocs(meetingsQuery);
      const meetingsList = meetingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledAt: doc.data().scheduledAt?.toDate(),
      })) as Meeting[];
      setMeetings(meetingsList.length > 0 ? meetingsList : mockMeetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setMeetings(mockMeetings);
    } finally {
      setIsLoading(false);
    }
  };

  const upcomingMeetings = meetings.filter(m => {
    const meetingDate = m.scheduledAt instanceof Date ? m.scheduledAt : new Date(m.scheduledAt);
    return m.status === 'scheduled' && meetingDate > new Date();
  }).sort((a, b) => {
    const dateA = a.scheduledAt instanceof Date ? a.scheduledAt : new Date(a.scheduledAt);
    const dateB = b.scheduledAt instanceof Date ? b.scheduledAt : new Date(b.scheduledAt);
    return dateA.getTime() - dateB.getTime();
  });

  const calendarEvents = meetings.map(m => {
    const start = m.scheduledAt instanceof Date ? m.scheduledAt : new Date(m.scheduledAt);
    const end = new Date(start.getTime() + m.duration * 60000);
    return {
      id: m.id,
      title: m.title,
      start,
      end,
      type: 'buyer_seller' as const,
      status: m.status === 'scheduled' ? 'confirmed' as const : 'completed' as const,
    };
  });

  const formatMeetingDate = (date: any) => {
    const d = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === now.toDateString()) return t.today;
    if (d.toDateString() === tomorrow.toDateString()) return t.tomorrow;
    return format(d, 'EEEE, d MMMM', { locale: language === 'fr' ? fr : undefined });
  };

  const formatMeetingTime = (date: any) => {
    const d = date instanceof Date ? date : new Date(date);
    return format(d, 'HH:mm');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <CalendarSyncButton />
          <Button onClick={() => setShowScheduleForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t.newMeeting}
          </Button>
        </div>
      </div>

      {/* Schedule Meeting Form */}
      <ScheduleMeetingForm
        open={showScheduleForm}
        onOpenChange={setShowScheduleForm}
        partnerType={partnerType}
        onMeetingCreated={loadMeetings}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar">{t.calendarView}</TabsTrigger>
          <TabsTrigger value="meetings">{t.upcomingMeetings}</TabsTrigger>
          <TabsTrigger value="availability">{t.availability}</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <VisualCalendar meetings={calendarEvents as any} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meetings" className="mt-6">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {upcomingMeetings.length} {t.scheduledMeetings}
            </p>

            {upcomingMeetings.length > 0 ? (
              <div className="space-y-4">
                {upcomingMeetings.map((meeting) => (
                  <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="default">{t.scheduled}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {formatMeetingDate(meeting.scheduledAt)}
                        </span>
                      </div>
                      <CardTitle className="text-lg">{meeting.title}</CardTitle>
                      {meeting.description && (
                        <CardDescription>{meeting.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Client</p>
                            <p className="text-sm font-medium">{meeting.clientName}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">{t.duration}</p>
                            <p className="text-sm font-medium">
                              {formatMeetingTime(meeting.scheduledAt)} • {meeting.duration} {t.minutes}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Participants</p>
                            <p className="text-sm font-medium">{meeting.attendees.length}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button size="sm" className="flex-1" asChild>
                          <a href={meeting.meetingLink} target="_blank" rel="noopener noreferrer">
                            <Video className="h-4 w-4 mr-1" />
                            {t.joinMeeting}
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1">
                          {t.viewDetails}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">{t.noMeetings}</h3>
                  <Button className="mt-4">
                    <Plus className="mr-2 h-4 w-4" />
                    {t.newMeeting}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="availability" className="mt-6">
          <AvailabilitySettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
